import style from './Button.module.scss';
import clsx from 'clsx';
import type React from 'react';

type CommonClickEvent = React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

type ButtonProps = {
  children: React.ReactNode;
  size?: 'lg' | 'md' | 'sm';
  color?: 'red' | 'green' | 'orange' | 'dark' | 'light' | 'gray' | 'brand' | 'disabled';
  width?: 'fit' | 'expand';

  /** 正常可點時才觸發（會帶 event） */
  onClick?: (e: CommonClickEvent) => void;

  /** disabled 狀態下點擊時的替代行為（會帶 event） */
  onDisabledClick?: (e: CommonClickEvent) => void;

  as?: 'button' | 'a';
  href?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

const Button = ({
  children,
  size = 'md',
  color = 'red',
  width = 'fit',
  onClick,
  onDisabledClick,
  as = 'button',
  href,
  disabled = false,
  type = 'button',
}: ButtonProps) => {
  const classes = clsx(
    style.button,
    style[`button-${size}`],
    style[`button-${color}`],
    style[`button-${width}`]
  );

  if (as === 'a') {
    const handleAnchorClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        onDisabledClick?.(e);
        return;
      }

      onClick?.(e);
    };

    return (
      <a
        href={href}
        className={classes}
        onClick={handleAnchorClick}
        aria-disabled={disabled ? 'true' : undefined}
        tabIndex={disabled ? -1 : undefined}
      >
        {children}
      </a>
    );
  }

  // as="button"
  const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) {
      // 一般情況下，disabled button 不會觸發 onClick
      // 但保險起見：若有觸發到，直接吃掉
      e.preventDefault();
      e.stopPropagation();
      onDisabledClick?.(e);
      return;
    }

    onClick?.(e);
  };

  const handleButtonClickCapture: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    // disabled button 不會觸發 onClick，但 capture 階段仍可攔到（用來做提示/登入）
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      onDisabledClick?.(e);
    }
  };

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      onClick={handleButtonClick}
      onClickCapture={handleButtonClickCapture}
    >
      {children}
    </button>
  );
};

export default Button;
